// ── customAI — своя нейросеть для объяснений и разборов слов ─────────────────
// Любой сервис с API, совместимым с OpenAI (POST {адрес}/chat/completions):
// OpenAI, DeepSeek, Groq, Google Gemini, OpenRouter, Mistral, Together, а также
// Ollama и LM Studio на своём компьютере. Адрес, ключ и модель хранятся только
// на этом устройстве (localStorage) и уходят только в выбранный сервис.

import { t } from './i18n';
import { markSetting } from './settings';

const KEY = 'ac_ai_custom';

export const AI_PRESETS = [
  { id: 'openai',     name: 'OpenAI',       baseUrl: 'https://api.openai.com/v1',                                 model: 'gpt-4o-mini',                       keyUrl: 'https://platform.openai.com/api-keys' },
  { id: 'deepseek',   name: 'DeepSeek',     baseUrl: 'https://api.deepseek.com/v1',                               model: 'deepseek-chat',                     keyUrl: 'https://platform.deepseek.com/api_keys' },
  { id: 'gemini',     name: 'Google Gemini', baseUrl: 'https://generativelanguage.googleapis.com/v1beta/openai',  model: 'gemini-2.0-flash',                  keyUrl: 'https://aistudio.google.com/apikey' },
  { id: 'groq',       name: 'Groq',         baseUrl: 'https://api.groq.com/openai/v1',                            model: 'llama-3.3-70b-versatile',           keyUrl: 'https://console.groq.com/keys' },
  { id: 'openrouter', name: 'OpenRouter',   baseUrl: 'https://openrouter.ai/api/v1',                              model: 'meta-llama/llama-3.3-70b-instruct:free', keyUrl: 'https://openrouter.ai/keys' },
  { id: 'mistral',    name: 'Mistral',      baseUrl: 'https://api.mistral.ai/v1',                                 model: 'mistral-small-latest',              keyUrl: 'https://console.mistral.ai/api-keys' },
  { id: 'ollama',     name: 'Ollama (на своём компьютере)', baseUrl: 'http://localhost:11434/v1',                model: 'llama3.1',                          keyUrl: '' },
  { id: 'lmstudio',   name: 'LM Studio (на своём компьютере)', baseUrl: 'http://localhost:1234/v1',              model: '',                                  keyUrl: '' },
];

export function getCustomAI() {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || 'null');
    return v && typeof v === 'object' ? { baseUrl: v.baseUrl || '', key: v.key || '', model: v.model || '', use: v.use !== false } : { baseUrl: '', key: '', model: '', use: false };
  } catch { return { baseUrl: '', key: '', model: '', use: false }; }
}
export function setCustomAI(v) {
  try {
    if (!v || !v.baseUrl) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, JSON.stringify({ baseUrl: v.baseUrl.trim().replace(/\/+$/, ''), key: (v.key || '').trim(), model: (v.model || '').trim(), use: v.use !== false }));
  } catch {}
  markSetting(KEY);
}
export const isCustomAIReady = () => { const c = getCustomAI(); return !!(c.use && c.baseUrl && c.model); };

function headers(c) {
  const h = { 'Content-Type': 'application/json' };
  if (c.key) h.Authorization = `Bearer ${c.key}`;
  if (/openrouter\.ai/.test(c.baseUrl)) { h['HTTP-Referer'] = 'https://audiocards.vercel.app'; h['X-Title'] = 'AntonCards'; }
  return h;
}
async function errorText(res) {
  let msg = '';
  try { const j = await res.json(); msg = j?.error?.message || j?.message || JSON.stringify(j).slice(0, 200); } catch { try { msg = (await res.text()).slice(0, 200); } catch {} }
  if (res.status === 401 || res.status === 403) return t('Ключ не подошёл');
  if (res.status === 404) return t('Нет такой модели или адреса: {0}', [msg || res.status]);
  if (res.status === 429) return t('Слишком много запросов. Подождите минуту.');
  return `${res.status}: ${msg}`;
}

// Один ответ целиком. json=true — просим вернуть JSON-объект.
export async function chatOnce({ prompt, system, json = false, maxTokens = 4000, signal }) {
  const c = getCustomAI();
  const body = { model: c.model, temperature: 0.4, max_tokens: maxTokens,
    messages: [...(system ? [{ role: 'system', content: system }] : []), { role: 'user', content: prompt }] };
  const send = async (b) => {
    try { return await fetch(`${c.baseUrl}/chat/completions`, { method: 'POST', headers: headers(c), body: JSON.stringify(b), signal }); }
    catch (e) { if (e?.name === 'AbortError') throw e; throw new Error(t('Нет связи с нейросетью ({0}). Проверьте адрес и интернет.', [c.baseUrl])); }
  };
  let res = await send(json ? { ...body, response_format: { type: 'json_object' } } : body);
  if (!res.ok && json && res.status === 400) res = await send(body); // сервис не знает response_format — просим текстом
  if (!res.ok) throw new Error(await errorText(res));
  const data = await res.json();
  return data?.choices?.[0]?.message?.content || '';
}

// Потоковый ответ — кусочками (для объяснений в читалке).
export async function* streamChat({ prompt, signal }) {
  const c = getCustomAI();
  let res;
  try {
    res = await fetch(`${c.baseUrl}/chat/completions`, { method: 'POST', headers: headers(c), signal,
      body: JSON.stringify({ model: c.model, stream: true, temperature: 0.4, max_tokens: 600, messages: [{ role: 'user', content: prompt }] }) });
  } catch (e) { if (e?.name === 'AbortError') throw e; throw new Error(t('Нет связи с нейросетью ({0}). Проверьте адрес и интернет.', [c.baseUrl])); }
  if (!res.ok || !res.body) throw new Error(await errorText(res));
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
    const events = buf.split('\n\n');
    buf = events.pop() || '';
    for (const ev of events) {
      const line = ev.split('\n').find((l) => l.startsWith('data:'));
      if (!line) continue;
      const payload = line.slice(5).trim();
      if (!payload || payload === '[DONE]') continue;
      try { const d = JSON.parse(payload)?.choices?.[0]?.delta?.content; if (d) yield d; } catch {}
    }
  }
}

// JSON из ответа (модель могла обернуть его в текст или ```json).
export function parseJsonLoose(text) {
  try { return JSON.parse(text); } catch {}
  const a = text.indexOf('{'), b = text.lastIndexOf('}');
  if (a >= 0 && b > a) { try { return JSON.parse(text.slice(a, b + 1)); } catch {} }
  throw new Error(t('Нейросеть ответила не в том формате. Попробуйте ещё раз или другую модель.'));
}

export async function checkCustomAI() {
  const out = await chatOnce({ prompt: 'Reply with the single word: OK', maxTokens: 10 });
  if (!out) throw new Error(t('Пустой ответ — проверьте название модели.'));
  return true;
}
